import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Truck, Leaf, HeartHandshake } from "lucide-react";
import { api } from "@/lib/api";
import Reveal, { MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Testimonials from "@/components/Testimonials";
import SEO from "@/lib/seo";
import { useSettings } from "@/lib/settings";
import { useContent, Em, CmsImg } from "@/lib/content";
import { formatDate, formatPrice } from "@/lib/format";
import { EASE } from "@/lib/motion";


export default function Home() {
  const settings = useSettings();
  const { t, list } = useContent();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    api.get("/products?limit=8").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/posts?limit=3").then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.featured).slice(0, 6);
  const spotlight = featured[0];


  return (
    <div data-testid="home-page">
      <SEO description={settings.seo_description} />

      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden bg-white pt-28 pb-16">
        <motion.span
          aria-hidden
          style={{ y: blobY }}
          className="absolute -top-32 -right-40 h-[34rem] w-[34rem] rounded-full bg-mint/25 blur-3xl"
        />
        <motion.span
          aria-hidden
          style={{ y: imgY }}
          className="absolute bottom-0 -left-40 h-[28rem] w-[28rem] rounded-full bg-rose/30 blur-3xl"
        />
        {t("home.hero.watermark") && (
          <span aria-hidden className="text-outline font-serif italic absolute bottom-4 left-1/2 -translate-x-1/2 text-[13vw] leading-none select-none pointer-events-none whitespace-nowrap">
            {t("home.hero.watermark")}
          </span>
        )}

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.p
              className="eyebrow mb-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {settings.hero_overline}
            </motion.p>
            <h1 className="font-serif text-teal-dark text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[1.04] tracking-tight">
              <MaskedLine delay={0.2}>{settings.hero_title_1}</MaskedLine>
              <MaskedLine delay={0.35}>
                <em className="text-teal">{settings.hero_title_2}</em>
                <span className="text-mint">.</span>
              </MaskedLine>
            </h1>
            <motion.p
              className="mt-6 max-w-xl text-base md:text-lg text-ink/80 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            >
              {settings.hero_sub}
            </motion.p>
            <motion.div
              className="mt-9 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
            >
              <Link
                data-testid="hero-cta-shop-now"
                to="/boutique"
                className="group inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 text-sm font-medium text-cream shadow-[0_12px_30px_rgba(16,114,105,0.3)] transition-all hover:bg-teal-dark hover:shadow-[0_16px_40px_rgba(16,114,105,0.4)] active:scale-[0.98]"
              >
                {t("home.hero.cta")}
                <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                data-testid="hero-cta-journal"
                to="/conseils"
                className="link-underline text-sm font-medium text-teal"
              >
                {t("home.hero.cta2")}
              </Link>
            </motion.div>

            <motion.ul
              className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink/70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.95 }}
            >
              <li className="flex items-center gap-2"><Truck size={16} className="text-teal" />{t("home.hero.badge1")}</li>
              <li className="flex items-center gap-2"><Leaf size={16} className="text-teal" />{t("home.hero.badge2")}</li>
              <li className="flex items-center gap-2"><HeartHandshake size={16} className="text-teal" />{t("home.hero.badge3")}</li>
            </motion.ul>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: EASE }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="relative">
                <div className="arch overflow-hidden shadow-[0_30px_80px_rgba(16,114,105,0.22)] border-[6px] border-white">
                  <CmsImg imgKey="img.home.hero" alt="Maman tenant tendrement son bébé" fetchPriority="high" className="w-full h-[420px] md:h-[540px]" />
                </div>
                <motion.div
                  className="absolute -bottom-8 -left-6 md:-left-12 w-40 md:w-52 overflow-hidden rounded-2xl border-4 border-white shadow-xl hidden sm:block"
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                >
                  <CmsImg imgKey="img.home.hero2" alt="Maman allaitant son bébé en douceur" className="w-full h-36 md:h-44" />
                </motion.div>
              </motion.div>
              <motion.div
                data-testid="hero-badge-delivery"
                className="absolute -top-6 -right-3 md:-right-8 rounded-2xl bg-white border border-line px-5 py-4 shadow-xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mauve">{t("home.hero.pill_label")}</p>
                <p className="font-serif text-2xl text-teal">{t("home.hero.pill_value")}</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <Marquee items={list("home.marquee")} />

      {/* ============ CATEGORIES ============ */}
      <section className="py-20 md:py-28 bg-cream-light">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="eyebrow mb-3">{t("home.cats.eyebrow")}</p>
              <h2 className="font-serif text-3xl md:text-5xl text-teal-dark">{t("home.cats.title")}</h2>
            </div>
            <Link data-testid="home-categories-all" to="/boutique" className="link-underline text-sm font-medium text-teal inline-flex items-center gap-1">
              {t("home.cats.link")} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {categories.map((cat, i) => (
              <Reveal key={cat.id} delay={i * 0.06}>
                <Link
                  data-testid={`home-category-${cat.slug}`}
                  to={`/boutique?categorie=${cat.slug}`}
                  className="group block"
                >
                  <div className={`arch overflow-hidden border-4 border-white shadow-md transition-transform duration-500 group-hover:-translate-y-2 ${["bg-mint/30", "bg-rose/40", "bg-sky2/30", "bg-tan/40", "bg-mauve/25", "bg-mint/20"][i % 6]}`}>
                    {cat.image && <img src={cat.image} alt={cat.name} loading="lazy" className="w-full h-40 md:h-52 object-cover mix-blend-multiply" />}
                  </div>
                  <p className="mt-3 font-serif text-lg text-teal-dark leading-tight">{cat.name}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TEASER ACCOMPAGNEMENT ============ */}
      <section data-testid="home-services-teaser" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-cream-light border border-line grid grid-cols-1 lg:grid-cols-12">
              <span aria-hidden className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-mint/30 blur-3xl" />
              <div className="relative lg:col-span-7 p-8 md:p-14">
                <p className="eyebrow mb-4">{t("teaser.eyebrow")}</p>
                <h2 className="font-serif text-3xl md:text-5xl text-teal-dark leading-tight">
                  <Em>{t("teaser.title")}</Em>
                </h2>
                <p className="mt-5 text-ink/75 max-w-xl leading-relaxed">
                  {t("teaser.text")}
                </p>
                <ul className="mt-6 flex flex-wrap gap-2.5">
                  {list("teaser.tags").map((tag) => (
                    <li key={tag} className="rounded-full bg-white border border-line px-4 py-1.5 text-xs font-medium text-teal-dark">{tag}</li>
                  ))}
                </ul>
                <div className="mt-9 flex flex-wrap items-center gap-5">
                  <Link
                    data-testid="home-services-cta"
                    to="/accompagnement"
                    className="inline-flex items-center gap-2 rounded-full bg-teal px-8 py-3.5 font-medium text-cream shadow-[0_12px_30px_rgba(16,114,105,0.25)] hover:bg-teal-dark transition-all active:scale-[0.98]"
                  >
                    {t("teaser.cta")} <ArrowRight size={17} />
                  </Link>
                  <p className="text-sm text-ink/60">{t("teaser.price_note", { prix: formatPrice(settings.svc_visio_price) })}</p>
                </div>
              </div>
              <div className="relative lg:col-span-5 min-h-[18rem]">
                <CmsImg imgKey="img.home.teaser" alt="Esther, conseillère en lactation, accompagne une maman à Dakar" className="absolute inset-0 h-full w-full" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ SPOTLIGHT ============ */}
      {spotlight && (
        <section className="py-20 md:py-28 bg-cream">
          <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="overflow-hidden rounded-[2rem] shadow-[0_30px_80px_rgba(16,114,105,0.18)]">
                  <motion.img
                    src={spotlight.images?.[0]}
                    alt={spotlight.name}
                    style={{ y: imgY }}
                    className="w-full h-[380px] md:h-[520px] object-cover"
                  />
                </div>
                <span className="absolute -top-4 -right-4 rounded-full bg-rose px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-teal-dark rotate-3">
                  {t("home.spotlight.badge")}
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="eyebrow mb-4">{t("home.spotlight.eyebrow")}</p>
              <h2 className="font-serif text-3xl md:text-5xl text-teal-dark leading-tight">{spotlight.name}</h2>
              <p className="mt-5 text-ink/80 leading-relaxed line-clamp-4">{spotlight.description}</p>
              <p className="mt-6 font-semibold text-2xl text-teal">{spotlight.price ? `${spotlight.price.toLocaleString("fr-FR").replace(/[ ,\u202f\u00a0]/g, ".")} FCFA` : ""}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  data-testid="spotlight-cta"
                  to={`/produit/${spotlight.slug}`}
                  className="group inline-flex items-center gap-2 rounded-full bg-teal px-8 py-4 text-sm font-medium text-cream hover:bg-teal-dark transition-all active:scale-[0.98]"
                >
                  {t("home.spotlight.cta")}
                  <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ============ TÉMOIGNAGES ============ */}
      <Testimonials />

      {/* ============ CONSEILS TEASER ============ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <p className="eyebrow mb-3">{t("home.blog.eyebrow")}</p>
              <h2 className="font-serif text-3xl md:text-5xl text-teal-dark">{t("home.blog.title")}</h2>
            </div>
            <Link data-testid="home-blog-all" to="/conseils" className="link-underline text-sm font-medium text-teal inline-flex items-center gap-1">
              {t("home.blog.link")} <ArrowUpRight size={16} />
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {posts.map((post, i) => (
              <Reveal key={post.id} delay={i * 0.08}>
                <Link data-testid={`home-post-${post.slug}`} to={`/conseils/${post.slug}`} className="group block">
                  <div className="overflow-hidden rounded-3xl">
                    {post.cover && (
                      <img src={post.cover} alt={post.title} loading="lazy" className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105" />
                    )}
                  </div>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-mauve">{formatDate(post.created_at)}</p>
                  <h3 className="mt-2 font-serif text-xl md:text-2xl text-teal-dark leading-snug group-hover:text-teal transition-colors">{post.title}</h3>
                  <p className="mt-2 text-sm text-ink/70 line-clamp-2">{post.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-20 md:py-28 bg-rose/50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <p className="eyebrow mb-4">{t("home.cta.eyebrow")}</p>
            <h2 className="font-serif text-3xl md:text-6xl text-teal-dark leading-tight">
              <Em className="">{t("home.cta.title")}</Em>
            </h2>
            <p className="mt-5 text-ink/75 max-w-xl mx-auto">
              {t("home.cta.text")}
            </p>
            <Link
              data-testid="home-cta-boutique"
              to="/boutique"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-teal px-10 py-4 font-medium text-cream shadow-[0_12px_30px_rgba(16,114,105,0.3)] hover:bg-teal-dark transition-all active:scale-[0.98]"
            >
              {t("home.cta.button")} <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
